import React, {Fragment} from 'react';
import Icon, {IconProps} from './Icon';
import {Tooltip, TooltipProps} from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import classNames from 'classnames';
import {connect} from "src/store";

type ButtonState = {
    timeout: number,
    disabled: boolean,
    opened: boolean,
}

type IconPropsType = IconProps & {position?: string};

export type ButtonProps = {
    tag?: any,
    type?: string,
    color?: string|"default"|"warning"|"danger"|"success",
    disabled?: boolean,
    timeout?: number,
    disableBeforeTimeout?: boolean,
    timeoutTick?: number,
    showTimeout?: boolean,
    focus?: boolean,
    onClick?: (event: Event) => void,
    onMouseDown?: (event: Event) => void,
    onMouseUp?: (event: Event) => void,
    onMouseMove?: (event: Event) => void,
    onBlur?: (event: Event) => void,
    onFocus?: (event: Event) => void,
    icon?: string|IconPropsType,
    tooltip?: string|TooltipProps|(() => any)|Tooltip,
    className?: any,
    block?: boolean,
    flat?: string,
    size?: string,
    privileges?: string[],
    privilegesType?: "and"|"or",
    privilegeVisible?: boolean,
    actions?: any
    right?: boolean,
    user?: {
        privileges?: {name: string}[]
    },
}

class Button extends React.Component<ButtonProps, ButtonState> {

    state = {
        timeout: 0,
        disabled: false,
        opened: false
    };

    static defaultProps: ButtonProps = {
        privileges: [],
        privilegesType: "or",
        privilegeVisible: false,
        showTimeout: true,
        timeoutTick: 1000,
        disableBeforeTimeout: true,
        right: true
    }

    private button: any;
    private timeout: number;
    private documentClickListener: () => any;
    private dropdownClick: boolean;
    private panel: HTMLUListElement;

    componentDidUpdate(prevProps: ButtonProps, prevState: ButtonState, prevContext: any) {
        if (this.props.focus) {
            this.button && this.button.focus();
        }
        if (this.props.timeout !== prevProps.timeout) {
            this.timeoutProp();
        }
    }

    timeoutProp = () => {
        this.timeout && clearTimeout(this.timeout);
        this.setState(() => ({
            timeout: this.props.timeout,
            disabled: (this.props.disableBeforeTimeout === undefined || this.props.disableBeforeTimeout === true) && !!this.props.timeout
        }), () => {
            if (this.state.timeout) {
                const tick = () => window.setTimeout(() => {
                    this.setState({timeout: --this.state.timeout}, () => {
                        if (this.state.timeout <= 0) {
                            this.setState({disabled: false});
                        } else {
                            this.timeout = tick();
                        }
                    });
                }, this.props.timeoutTick);
                this.timeout = tick();
            } else {
                clearTimeout(this.timeout);
            }
        });
    }

    componentDidMount() {
        if (this.props.focus) {
            this.button && this.button.focus();
        }
        this.timeoutProp();
    }

    open = () => {
        if (this.state.opened) return;
        this.setState({opened: true});
        this.bindDocumentListener();
    }

    hide = () => {
        if (!this.state.opened) return;
        this.setState({opened: false});
        this.unbindDocumentListener();
    }

    bindDocumentListener() {
        if (!this.documentClickListener) {
            this.documentClickListener = () => {
                if (this.dropdownClick)
                    this.dropdownClick = false;
                else
                    this.hide();
            };

            document.addEventListener('click', this.documentClickListener);
        }
    }

    unbindDocumentListener() {
        if (this.documentClickListener) {
            document.removeEventListener('click', this.documentClickListener);
            this.documentClickListener = null;
        }
    }

    componentWillUnmount() {
        this.unbindDocumentListener();
    }

    actions = (props: ButtonProps) => {
        let actions = this.props.actions;
        if (Array.isArray(actions)) {
            actions = actions.map((child, idx) => {
                if (child !== null && child !== undefined && child instanceof Object && child.type) {
                    return React.createElement(child.type, {...child.props, key: idx});
                } else if (child !== null && child !== undefined && child instanceof Object) {
                    if (child.divider) {
                        return <li key={idx} role="separator" className="divider" />;
                    } else {
                        let props = {...child};
                        delete props.href;
                        delete props.label;
                        delete props.text;
                        delete props.content;
                        return <li key={idx}><a href={child.href || "#"} {...props} onClick={(event) => {
                            this.hide();
                            if (child.onClick) {
                                child.onClick(event);
                            }
                            if (!child.href && child.onClick) {
                                event.preventDefault();
                                return false;
                            }
                        }}>{child.label || child.text || child.content}</a></li>
                    }
                } else {
                    return child;
                }
            });
        } else if (actions !== null && actions !== undefined && actions instanceof Object && actions.type) {
            actions = React.createElement(actions.type, {...actions.props});
        }

        return <ul ref={(el) => this.panel = el} className={classNames("dropdown-menu", {"dropdown-menu-right": props.right})}>
            {actions}
        </ul>;
    }

    render() {
        let disabled = this.state.disabled || false;
        let privilegeVisible = true;
        if (!!this.props.privileges.length) {
            const privileges = [];
            this.props.user.privileges.map((p) => {
                if (this.props.privileges.indexOf(p.name) !== -1) {
                    privileges.push(p.name);
                }
            });
            if (this.props.privilegesType === "or") {
                disabled = privileges.length === 0;
            } else if (this.props.privilegesType === "and") {
                disabled = privileges.length < this.props.privileges.length;
            }
            privilegeVisible = this.props.privilegeVisible;
            if (disabled && !privilegeVisible) {
                return null;
            }
        }
        if (!disabled) {
            disabled = this.props.disabled;
        }
        const { className } = this.props;
        let tooltip;
        // @ts-ignore
        if (typeof(this.props.tooltip) === "object" && !(this.props.tooltip || {}).props) {
            // @ts-ignore
            tooltip = {...(this.props.tooltip || {}), position: (this.props.tooltip || {}).position || "top", disabled: (this.props.tooltip || {}).disabled || disabled};
        } else {
            const html = this.props.tooltip instanceof Function ? this.props.tooltip() : this.props.tooltip;
            tooltip = {position: "top", disabled: disabled};
            if (html) {
                // @ts-ignore
                tooltip.html = html;
            } else {
                tooltip = {};
            }
        }
        let icon: IconPropsType;
        if (this.props.icon instanceof Object) {
            // @ts-ignore
            icon = {name: null, ...this.props.icon, position: this.props.icon.position || "left", disabled: this.props.icon.disabled || disabled};
        } else {
            icon = {name: String(this.props.icon), position: "left", disabled: disabled};
        }
        const iconTag = this.props.icon && <Icon {...icon} />;
        const Tag = this.props.tooltip && !disabled ? Tooltip : Fragment;
        const ButtonTag = this.props.tag || "button";
        const type = this.props.type || "button";

        const props: ButtonProps = {...this.props};
        if (props.focus !== undefined && props.focus !== null) {
            // @ts-ignore
            props.focus = (props.focus || false).toString();
        }
        delete props['timeout'];
        delete props['timeoutTick'];
        delete props['showTimeout'];
        delete props['privilegesType'];
        delete props['privilegeVisible'];
        delete props['block'];
        delete props['actions'];
        delete props['right'];
        delete props['disableBeforeTimeout'];

        const button = <ButtonTag
            ref={(button: Button) => this.button = button}
            {...props}
            type={type}
            className={classNames("button btn", className, {"tooltip-holder": this.props.tooltip && !disabled, ["btn-" + this.props.size || ""]: this.props.size, "btn-flat": this.props.flat, "btn-block": this.props.block && !this.props.actions, disabled, ["btn-" + this.props.color]: this.props.color})}
            onClick={ disabled || !this.props.onClick ? null : this.props.onClick }
        >
            <Tag {...(this.props.tooltip && !disabled ? tooltip : {})}>
                {icon.position === "left" && <Fragment>{iconTag} </Fragment>}
                {this.props.children}
                {icon.position === "right" && <Fragment> {iconTag}</Fragment>}
                {this.props.showTimeout && this.state.timeout ? <Fragment> ({this.state.timeout})</Fragment> : null}
            </Tag>
        </ButtonTag>;

        if (this.props.actions) {
            const right = this.props.right === undefined ? true : this.props.right;
            const dropdownButton = <button
                type="button"
                className={classNames("btn dropdown-toggle", {["btn-" + this.props.size || ""]: this.props.size, "btn-flat": this.props.flat, ["btn-" + this.props.color]: this.props.color})}
                onClick={() => {
                    this.open();
                }}
            >
                <span className="caret" />
                <span className="sr-only">Toggle Dropdown</span>
            </button>;
            return (
                <div className={classNames("btn-group", {"btn-block": this.props.block, "open": this.state.opened})}>
                    {!right ? dropdownButton : null}
                    {button}
                    {right ? dropdownButton : null}
                    {this.actions({right})}
                </div>
            )
        }

        return button;
	}
}

export default connect((state: any) => {
    return {
        user: state.app.user
    };
}, {})(Button);